import React, { useCallback, useMemo } from 'react';
import { FunctionCallContent, parseApprovalRequest } from '../../../../libs/models/agui/FunctionCallContent';
import { parseHitlFields } from '../../../../libs/models/agui/HitlContent';
import { useAppDispatch, useAppSelector } from '../../../../redux/app/hooks';
import { RootState } from '../../../../redux/app/store';
import { AIModelName } from '../../../../libs/models/app/AIModel';
import { usePlugin } from '../../../../libs/hooks/usePlugin';
import { HitlInputCard } from './HitlInputCard';
import { MailApprovalCard } from './MailApprovalCard';
import { GenericApprovalCard } from './GenericApprovalCard';
import { ApprovalActions, SessionApprovalMode } from './ApprovalActions';

interface FunctionCallRendererProps {
  content: FunctionCallContent;
  isLast?: boolean;
}

/** 함수 이름으로 분기 */
export const FunctionCallRenderer: React.FC<FunctionCallRendererProps> = ({ content, isLast }) => {
  const dispatch = useAppDispatch();
  const agent = usePlugin();
  const { selectedId, conversations } = useAppSelector((s: RootState) => s.conversations_v2);
  const { selectedModel } = useAppSelector((s: RootState) => s.models);

  // 같은 callId의 응답 메시지에서 입력값 추출
  const answeredValues = useMemo<Record<string, string> | null>(() => {
    const msgs = conversations[selectedId]?.messages?.data ?? [];
    for (const m of msgs) {
      const fr = m.contents?.find(
        (c) =>
          (c as { $type?: string }).$type === 'functionResult' &&
          (c as { callId?: string }).callId === content.callId,
      ) as { result?: { 'approval-with-input'?: { value?: Record<string, string> } } } | undefined;
      if (fr) return fr.result?.['approval-with-input']?.value ?? {};
    }
    return null;
  }, [conversations, selectedId, content.callId]);

  const isAnswered = answeredValues !== null;
  const findApprovalResponse = useCallback(
    (matchKey: string): boolean | null => {
      const msgs = conversations[selectedId]?.messages?.data ?? [];
      for (const m of msgs) {
        const res = m.contents?.find((c) => {
          if ((c as { $type?: string }).$type !== 'toolApprovalResponse') return false;
          const r = c as { requestId?: string; toolCall?: { callId?: string } };
          return r.requestId === matchKey || r.toolCall?.callId === matchKey;
        }) as { approved?: boolean } | undefined;
        if (res) return res.approved === true;
      }
      return null;
    },
    [conversations, selectedId],
  );

  const submitResult = useCallback(
    (result: Record<string, unknown>, sessionMode?: SessionApprovalMode) => {
      dispatch({
        type: 'conversations/v2/submitFunctionResult',
        payload: {
          sessionId: selectedId,
          model: selectedModel.model as AIModelName,
          callId: content.callId,
          result,
          sessionApprovalMode: sessionMode,
        },
      });
      // HITL로 받은 변수는 1회성
      agent.resetInput();
    },
    [dispatch, agent, selectedId, selectedModel.model, content.callId],
  );

  const fields = useMemo(() => parseHitlFields(content.arguments.value), [content.arguments.value]);

  switch (content.name) {
    case 'request_approval': {
      const approval = parseApprovalRequest(content.arguments);
      if (!approval) return null;

      const approved = findApprovalResponse(approval.approval_id);
      const answered = approved !== null;
      const fa = approval.function_arguments;
      const isMail =
        approval.function_name === 'OutlookMailConnector_SendEmail' || approval.function_name === 'SendEmail';

      const respond = (ok: boolean, sessionMode?: SessionApprovalMode) => {
        submitResult({ approval_id: approval.approval_id, approved: ok }, sessionMode);
      };
      // 응답되면 버튼 대신 상태 표시, 미응답이고 마지막 턴이면 버튼 노출
      const footer =
        answered || isLast ? (
          <ApprovalActions
            onRespond={respond}
            answered={answered}
            approvedResult={approved === true}
            showAlways={!isMail}
          />
        ) : null;

      if (isMail) {
        return (
          <MailApprovalCard
            recipients={typeof fa.recipients === 'string' ? fa.recipients : undefined}
            subject={typeof fa.subject === 'string' ? fa.subject : undefined}
            content={typeof fa.content === 'string' ? fa.content : undefined}
            answered={answered}
            footer={footer}
          />
        );
      }

      return (
        <GenericApprovalCard
          functionName={approval.function_name}
          message={approval.message}
          functionArguments={fa}
          footer={footer}
        />
      );
    }
    // 발송 완료 카드는 승인 카드에서 이미 내용을 확인했으므로 표시하지 않음 (중복 방지)
    // case 'OutlookMailConnector_SendEmail': {
    //   // 거절된 건이면 완료 카드 숨김
    //   if (findApprovalResponse(content.callId) === false) return null;
    //   const args = content.arguments;
    //   return (
    //     <MailApprovalCard
    //       title="메일 발송 완료"
    //       recipients={typeof args.recipients === 'string' ? args.recipients : undefined}
    //       subject={typeof args.subject === 'string' ? args.subject : undefined}
    //       content={typeof args.content === 'string' ? args.content : undefined}
    //     />
    //   );
    // }
    case 'request_user_input': {
      const valueType = (content.arguments['value-type'] as string | undefined) ?? 'abclab-input';

      // 응답 완료 → read-only 카드 (위치 무관)
      if (isAnswered) {
        return (
          <HitlInputCard
            fields={fields}
            readOnly
            initialValues={answeredValues ?? undefined}
            collapsed={!isLast}
          />
        );
      }

      // 미응답이지만 마지막 메시지가 아니면 표시 안 함 (오래된 미응답)
      if (!isLast) return null;

      return (
        <HitlInputCard
          fields={fields}
          onSubmit={(values) => {
            submitResult({
              'approval-with-input': {
                approved: true,
                'value-type': valueType,
                value: values,
              },
            });
          }}
        />
      );
    }
    default:
      return null;
  }
};
